import { AppLayout } from '@app/AppLayout/AppLayout';
import { MinimalLayout } from '@app/AppLayout/MinimalLayout';
import { NotFound } from '@app/NotFound/NotFound';
import { ProtectedRoute } from '@app/layout/ProtectedRoute/ProtectedRoute';
import * as React from 'react';
import { Route, Routes } from 'react-router';

// Lazy load components
const HomePage = React.lazy(() => import('@app/HomePage/HomePage').then(module => ({ default: module.HomePage })));
const Assets = React.lazy(() => import('@app/Assets/Assets').then(module => ({ default: module.Assets })));
const AssetPlans = React.lazy(() => import('@app/AssetPlans/AssetPlans').then(module => ({ default: module.AssetPlans })));
const EventsView = React.lazy(() => import('@app/Events/EventsView').then((module) => ({ default: module.EventsView })));
const Monitoring = React.lazy(() => import('@app/Monitoring/Monitoring').then(module => ({ default: module.Monitoring })));
const DeploymentLogsAdmin = React.lazy(() => import('@app/DeploymentLogsAdmin/DeploymentLogsAdmin').then(module => ({ default: module.DeploymentLogsAdmin })));

export interface IAppRoute {
  label?: string; // Excluding the label will exclude the route from the nav sidebar in AppLayout
  element: React.ReactElement;
  exact?: boolean;
  path: string;
  title: string;
  routes?: IAppRoute[];
  defaultLabel?: string; // Default label for fallback (optional)
  defaultTitle: string; // Default title for fallback (required)
  requiredRoles?: string[];
}

export interface IAppRouteGroup {
  label: string;
  routes: IAppRoute[];
  title: string;
  defaultLabel: string; // Default label for fallback (required)
  defaultTitle: string; // Default title for fallback (required)
  requiredRoles?: string[];
}

export type AppRouteConfig = IAppRoute | IAppRouteGroup;

const routes: AppRouteConfig[] = [
  {
    element: <AppLayout><HomePage /></AppLayout>,
    label: 'routes.home.label',
    defaultLabel: 'Home',
    path: '/',
    title: 'routes.home.title',
    defaultTitle: 'NUADA | Home',
  },
  {
    element: <AppLayout><Assets /></AppLayout>,
    label: 'routes.assets.label',
    defaultLabel: 'Assets',
    path: '/nuada-assets',
    title: 'routes.assets.title',
    defaultTitle: 'NUADA | Assets',
  },
  {
    element: <AppLayout><AssetPlans /></AppLayout>,
    label: 'routes.assetPlans.label',
    defaultLabel: 'Asset Plans',
    path: '/nuada-assetplans',
    title: 'routes.assetPlans.title',
    defaultTitle: 'NUADA | Asset Plans',
  },
  {
    element: <AppLayout><EventsView /></AppLayout>,
    label: 'routes.events.label',
    defaultLabel: 'Events',
    path: '/nuada-events',
    title: 'routes.events.title',
    defaultTitle: 'NUADA | Events',
  },
  {
    element: <AppLayout><Monitoring /></AppLayout>,
    label: 'routes.monitoring.label',
    defaultLabel: 'Monitoring',
    path: '/nuada-monitoring',
    title: 'routes.monitoring.title',
    defaultTitle: 'NUADA | Monitoring',
  },
  {
    label: 'routes.admin.label',
    defaultLabel: 'Admin',
    title: 'routes.admin.title',
    defaultTitle: 'NUADA | Admin',
    requiredRoles: ['admin'],
    routes: [
      {
        element: <AppLayout><DeploymentLogsAdmin /></AppLayout>,
        label: 'routes.deploymentLogs.label',
        defaultLabel: 'Deployment Logs',
        path: '/nuada-deployment-logs',
        title: 'routes.deploymentLogs.title',
        defaultTitle: 'NUADA | Deployment Logs',
        requiredRoles: ['admin'],
      },
    ],
  },
  {
    element: <MinimalLayout><NotFound /></MinimalLayout>,
    path: '*',
    title: 'routes.notFound.title',
    defaultTitle: 'NUADA | Not Found',
  },
];

const flattenedRoutes: IAppRoute[] = routes.reduce(
  (flattened, route) => {
    if ('routes' in route) {
      return [...flattened, ...(route.routes ?? [])];
    }
    return [...flattened, route];
  },
  [] as IAppRoute[],
);

const AppRoutes = (): React.ReactElement => (
  <Routes>
    {flattenedRoutes.map(({ path, element, label, requiredRoles, defaultLabel, defaultTitle, title }) => {
      const key = `${path}-${label ?? defaultLabel ?? defaultTitle ?? title}`;

      if (requiredRoles && requiredRoles.length > 0) {
        return (
          <Route element={<ProtectedRoute requiredRoles={requiredRoles} />} key={key}>
            <Route path={path} element={element} />
          </Route>
        );
      }

      return <Route path={path} element={element} key={key} />;
    })}
    <Route path="*" element={<MinimalLayout><NotFound /></MinimalLayout>} />
  </Routes>
);

export { AppRoutes, routes };
